import type { IApiModelConfig, VulpApiModel, VulpApi } from '@vulp/js-api';

type VulpApiType = typeof VulpApi;

import('https://api.vulp.studio/v1/api.js')
	.then(async ({ VulpApi }: { VulpApi: VulpApiType }) => {
		await VulpApi.initialize();

		const model = await VulpApi.registerModel(
			document.querySelector('iframe') as HTMLIFrameElement,
		);

		model
			.onReady(({ model: modelConfig }: { model: IApiModelConfig }) => {
				createForm(modelConfig, model);
				selectColorButtons(modelConfig);
				selectCustomizeOptions(modelConfig);
			})
			.onModelUpdated(({ model }: { model: IApiModelConfig }) => {
				selectColorButtons(model);
				selectCustomizeOptions(model);
			});
	})
	.catch(err => {
		console.error(err);
	});

function selectColorButtons(model: IApiModelConfig): void {
	for (const [groupKey, colorGroup] of Object.entries(model.colorGroups)) {
		const { colors } = colorGroup;
		for (const [colorKey, color] of Object.entries(colors)) {
			const { active } = color;
			if (active) {
				const radioButton = document.querySelector(
					`input[name="${groupKey}"][value="${colorKey}"]`,
				) as HTMLInputElement | null;
				if (radioButton) {
					radioButton.checked = true;
				}
			}
		}
	}
}

function selectCustomizeOptions(model: IApiModelConfig): void {
	for (const [groupKey, customizeGroup] of Object.entries(
		model.customizeGroups ?? {},
	)) {
		const { options } = customizeGroup;
		for (const [optionKey, option] of Object.entries(options)) {
			const { active } = option;
			if (active) {
				const selectElement = document.querySelector(
					`select[name="${groupKey}"]`,
				);
				if (selectElement) {
					const option = selectElement.querySelector(
						`option[value="${optionKey}"]`,
					) as HTMLOptionElement | null;
					if (option) {
						option.selected = true;
					}
				}
			}
			if (active) {
				const radioButton = document.querySelector(
					`input[name="${groupKey}"][value="${optionKey}"]`,
				) as HTMLInputElement | null;
				if (radioButton) {
					radioButton.checked = true;
				}
			}
		}
	}
}

function updateOptions(event: Event, model: VulpApiModel): void {
	if (!model) {
		return;
	}

	const target = event.target as HTMLInputElement | null;

	if (target) {
		model.changeCustomizeOption(target.name, target.value);
	}
}

function updateColors(event: Event, model: VulpApiModel): void {
	if (!model) {
		return;
	}

	const target = event.target as HTMLInputElement | null;

	if (target) {
		model.changeColor(target.name, target.value);
	}
}

function createForm(modelConfig: IApiModelConfig, model: VulpApiModel): void {
	const container = document.createElement('div');
	container.id = 'data';

	for (const groupName of Object.keys(modelConfig.colorGroups)) {
		const colorsDiv = document.createElement('div');
		colorsDiv.classList.add('colors');

		const heading = document.createElement('h2');
		heading.textContent = translate(groupName);
		colorsDiv.appendChild(heading);

		let count = 1;
		for (const [colorName, color] of Object.entries(
			modelConfig.colorGroups[groupName]?.colors ?? {},
		)) {
			const inputId = `${groupName}${count}`;
			const input = document.createElement('input');
			input.id = inputId;
			input.dataset['value'] = `${count}`;
			input.type = 'radio';
			input.name = groupName;
			input.value = colorName;

			if (color.active) {
				input.checked = true;
			}

			const label = document.createElement('label');
			label.classList.add(colorName);
			label.htmlFor = inputId;
			label.title = translate(colorName);

			const span = document.createElement('span');
			span.textContent = colorName;

			label.appendChild(span);

			colorsDiv.appendChild(input);
			colorsDiv.appendChild(label);

			count++;
		}

		container.appendChild(colorsDiv);
	}

	for (const [groupName, customizeGroup] of Object.entries(
		modelConfig.customizeGroups ?? {},
	)) {
		const { options } = customizeGroup;
		const optionKeys = Object.keys(options);

		if (optionKeys.length > 0) {
			const selectDiv = document.createElement('div');
			selectDiv.classList.add('select');

			const heading = document.createElement('h2');
			heading.textContent = translate(groupName);
			selectDiv.appendChild(heading);

			const select = document.createElement('select');
			select.name = groupName;

			for (const optionKey of optionKeys) {
				const optionName = optionKey.replace(/-/g, ' ');
				const index = optionKeys.indexOf(optionKey);

				const option = document.createElement('option');
				option.dataset['value'] = `${index * 100}`;
				option.value = optionKey;
				option.textContent = translate(optionName);

				select.appendChild(option);
			}

			selectDiv.appendChild(select);
			container.appendChild(selectDiv);
		}
	}

	const data = document.getElementById('data');
	if (data) {
		data.replaceWith(container);
	}

	addEventListenersToGroups(model);
}

function addEventListenersToGroups(model: VulpApiModel): void {
	document.querySelectorAll('.colors input[type="radio"]').forEach(input => {
		input.addEventListener('change', event => updateColors(event, model));
	});

	document.querySelectorAll('select').forEach(select => {
		select.addEventListener('change', event => updateOptions(event, model));
	});
	document
		.querySelectorAll('.buttons input[type="checkbox"]')
		.forEach(input => {
			input.addEventListener('change', event =>
				updateOptions(event, model),
			);
		});
}

interface VulpTranslations {
	en: { [key: string]: string };
	nl: { [key: string]: string };
}

/* Active Translation function */
const vulpTranslations: VulpTranslations = {
	en: {
		seat: 'Seat',
		body: 'Body',
		mirrors: 'Mirrors',
		'mirror on': 'Mirrors On',
		'mirror off': 'No Mirrors',
		'bar-seat': 'Bar Seat',
		'bar seat small': 'Small Bar Seat',
		'bar seat large': 'Large Bar Seat',
		'bar seat off': 'No Bar Seat',
		rack: 'Rack',
		'rack on': 'Rack On',
		'rack off': 'No Rack',
		black: 'Black',
		brown: 'Brown',
		cognac: 'Cognac',
		creme: 'Cream',
		green: 'Green',
		blue: 'Blue',
		red: 'Red',
		orange: 'Orange',
	},
	nl: {
		seat: 'Zitting',
		body: 'Carrosserie',
		mirrors: 'Spiegels',
		'mirror on': 'Spiegels Aan',
		'mirror off': 'Geen Spiegels',
		'bar-seat': 'Zitbeugel',
		'bar seat small': 'Kleine Zitbeugel',
		'bar seat large': 'Grote Zitbeugel',
		'bar seat off': 'Geen Zitbeugel',
		rack: 'Bagagedrager',
		'rack on': 'Bagagedrager Aan',
		'rack off': 'Geen Bagagedrager',
		black: 'Zwart',
		brown: 'Bruin',
		cognac: 'Cognac',
		creme: 'Crème',
		green: 'Groen',
		blue: 'Blauw',
		red: 'Rood',
		orange: 'Oranje',
	},
};

function translate(term: string): string {
	const language = document.documentElement.lang ?? 'en';
	if (language !== 'en' && language !== 'nl') {
		return term;
	}

	const translations = vulpTranslations[language];
	return translations[term] ?? term;
}
